import { useState, useEffect } from 'react';
import { PieChart, RefreshCw, Edit3, X, Wallet, ChevronRight, ArrowLeft, History } from 'lucide-react';
import { portfolioAPI, brokersAPI, priceHistoryAPI } from '../api/client';
import { formatCurrency, formatPercent } from '../utils/formatters';

// ─── Price History View Modal ─────────────────────────────────────────────────

const PriceHistoryViewModal = ({ assetId, assetSymbol, onClose }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    priceHistoryAPI.list(assetId)
      .then((res) => setRecords(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .finally(() => setLoading(false));
  }, [assetId]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Historial de precios</h2>
            <p className="text-sm text-gray-500">{assetSymbol}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <History className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Sin historial aún. Actualiza el precio para registrarlo.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Fecha', 'Hora', 'Precio'].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {records.map((r) => {
                  const ts = r.created_at ? new Date(r.created_at) : null;
                  return (
                    <tr key={r.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 font-medium text-gray-800">{r.date}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {ts ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{formatCurrency(parseFloat(r.close_price))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Update Price Modal ──────────────────────────────────────────────────────

const UpdatePriceModal = ({ position, onClose, onSave }) => {
  const [price, setPrice] = useState(position.current_price || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(price);
    if (!price || isNaN(val) || val < 0) {
      setError('Ingresa un valor válido mayor o igual a 0.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSave(position.id, val);
      onClose();
    } catch {
      setError('No se pudo actualizar el precio.');
    } finally {
      setLoading(false);
    }
  };

  const currentValue = parseFloat(price || 0) * parseFloat(position.quantity);
  const invested = parseFloat(position.total_invested || 0);
  const gain = currentValue - invested;
  const gainPct = invested > 0 ? (gain / invested) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Actualizar precio</h2>
            <p className="text-sm text-gray-500">{position.asset_symbol} · {position.broker_name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor actual del activo</label>
            <input
              type="number"
              value={price}
              onChange={(e) => { setPrice(e.target.value); setError(null); }}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="0.00"
              min="0"
              step="any"
              autoFocus
            />
            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          </div>
          {price && (
            <div className="bg-gray-50 rounded-xl p-3 space-y-1 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Total position value</span>
                <span className="font-medium text-gray-900">{formatCurrency(currentValue)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Invested</span>
                <span className="font-medium text-gray-900">{formatCurrency(invested)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-1">
                <span className="text-gray-500">Return</span>
                <span className={`font-bold ${gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {gain >= 0 ? '+' : ''}{formatCurrency(gain)} ({gain >= 0 ? '+' : ''}{gainPct.toFixed(2)}%)
                </span>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition">
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const investmentAge = (dateStr) => {
  if (!dateStr) return null;
  const start = new Date(dateStr);
  const now = new Date();
  const days = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months}m`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths > 0 ? `${years}a ${remMonths}m` : `${years}a`;
};

// ─── Reusable summary card ────────────────────────────────────────────────────

const SummaryCard = ({ title, totalInvested, availableCash, currentValue, totalWealth, gainLoss, gainLossPct }) => {
  const glSign = gainLoss >= 0 ? '+' : '';
  const glColor = gainLoss >= 0 ? 'text-green-300' : 'text-red-300';
  return (
    <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
      {title && <p className="text-white font-bold text-base mb-4">{title}</p>}
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <p className="text-blue-200 text-xs mb-1">Total Invested</p>
          <p className="text-xl font-bold">{formatCurrency(totalInvested)}</p>
        </div>
        <div>
          <p className="text-blue-200 text-xs mb-1">Available</p>
          <p className="text-xl font-bold text-green-300">{formatCurrency(availableCash)}</p>
        </div>
        <div>
          <p className="text-blue-200 text-xs mb-1">Portfolio</p>
          <p className="text-xl font-bold">{formatCurrency(currentValue)}</p>
        </div>
        <div>
          <p className="text-blue-200 text-xs mb-1">Total</p>
          <p className="text-xl font-bold text-yellow-300">{formatCurrency(totalWealth)}</p>
        </div>
      </div>
      <div className="bg-white/10 rounded-xl p-3">
        <p className="text-blue-200 text-xs mb-1">Unrealized Return</p>
        <p className={`text-lg font-bold ${glColor}`}>
          {glSign}{formatCurrency(Math.abs(gainLoss))}
          <span className="text-sm ml-2">({glSign}{formatPercent(gainLossPct)})</span>
        </p>
      </div>
    </div>
  );
};

// ─── Position card ────────────────────────────────────────────────────────────

const PositionCard = ({ pos, onUpdatePrice, onViewHistory }) => {
  const isSold = pos.status === 'sold';
  const gl = parseFloat(pos.gain_loss || 0);
  const glClass = gl >= 0 ? 'text-green-600' : 'text-red-600';
  const glSign = gl >= 0 ? '+' : '';
  const invested = parseFloat(pos.average_cost || 0) * parseFloat(pos.quantity || 0)
    || parseFloat(pos.total_invested || 0);

  if (isSold) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 opacity-75">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="font-bold text-gray-500">{pos.asset_symbol}</p>
              <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full font-medium">Sold</span>
            </div>
            <p className="text-xs text-gray-400">{pos.asset_name}</p>
            <p className="text-xs text-gray-400">{pos.broker_name}</p>
          </div>
          <div className="text-right text-xs text-gray-400">
            <p>Avg. cost <span className="font-medium text-gray-500">{formatCurrency(parseFloat(pos.average_cost || 0))}</span></p>
            <p className="mt-0.5">Held <span className="font-medium">{investmentAge(pos.first_transaction_date) || '—'}</span></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="font-bold text-gray-900">{pos.asset_symbol}</p>
          <p className="text-xs text-gray-500">{pos.asset_name}</p>
          <p className="text-xs text-gray-400">{pos.broker_name}</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-gray-900">{formatCurrency(parseFloat(pos.current_value || 0))}</p>
          <p className={`text-sm font-medium ${glClass}`}>{glSign}{formatCurrency(Math.abs(gl))}</p>
          <p className={`text-xs ${glClass}`}>{glSign}{formatPercent(pos.gain_loss_percentage || 0)}</p>
          <div className="flex items-center gap-3 justify-end mt-1">
            <button
              onClick={() => onViewHistory(pos)}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-indigo-600 transition"
            >
              <History className="w-3 h-3" /> Historial
            </button>
            <button
              onClick={() => onUpdatePrice(pos)}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition"
            >
              <Edit3 className="w-3 h-3" /> Actualizar precio
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs text-gray-500">
        <div>
          <p>Cantidad</p>
          <p className="font-medium text-gray-700">{parseFloat(pos.quantity).toFixed(4)}</p>
        </div>
        <div>
          <p>Avg. cost</p>
          <p className="font-medium text-gray-700">{formatCurrency(parseFloat(pos.average_cost || 0))}</p>
        </div>
        <div>
          <p>Invested</p>
          <p className="font-medium text-gray-700">{formatCurrency(invested)}</p>
        </div>
        <div>
          <p>Held</p>
          <p className="font-medium text-gray-700">{investmentAge(pos.first_transaction_date) || '—'}</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main screen ──────────────────────────────────────────────────────────────

const PortfolioScreen = () => {
  const [goalGroups, setGoalGroups] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [updatingPosition, setUpdatingPosition] = useState(null);
  const [historyPosition, setHistoryPosition] = useState(null);

  useEffect(() => { fetchPortfolioData(); }, []);

  const fetchPortfolioData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [byGoalRes, brokersRes] = await Promise.all([
        portfolioAPI.byGoal(),
        brokersAPI.list(),
      ]);
      setGoalGroups(byGoalRes.data);
      setBrokers(brokersRes.data.results || brokersRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load portfolio data.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrice = async (id, currentPrice) => {
    await portfolioAPI.updatePrice(id, currentPrice);
    await fetchPortfolioData();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchPortfolioData} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition">
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  // ── Goal detail view ──────────────────────────────────────────────────────
  if (selectedGoalId !== null) {
    const group = goalGroups.find((g) => g.goal_id === selectedGoalId);
    if (!group) return null;

    const availableCash = parseFloat(group.available_cash || 0);
    const currentValue = parseFloat(group.current_value || 0);

    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-4">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <button
              onClick={() => setSelectedGoalId(null)}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{group.goal_name}</h1>
              <p className="text-gray-400 text-xs">Portfolio · {group.positions.length} posición{group.positions.length !== 1 ? 'es' : ''}</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
          <SummaryCard
            totalInvested={parseFloat(group.total_invested || 0)}
            availableCash={availableCash}
            currentValue={currentValue}
            totalWealth={parseFloat(group.total_wealth || 0)}
            gainLoss={parseFloat(group.gain_loss || 0)}
            gainLossPct={group.gain_loss_percentage || 0}
          />

          {group.positions.length === 0 ? (
            <div className="text-center py-12">
              <PieChart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Sin posiciones en este goal</p>
            </div>
          ) : (
            <div className="space-y-3">
              {group.positions.map((pos) => (
                <PositionCard key={pos.id} pos={pos} onUpdatePrice={setUpdatingPosition} onViewHistory={setHistoryPosition} />
              ))}
            </div>
          )}
        </div>

        {updatingPosition && (
          <UpdatePriceModal
            position={updatingPosition}
            onClose={() => setUpdatingPosition(null)}
            onSave={handleUpdatePrice}
          />
        )}
        {historyPosition && (
          <PriceHistoryViewModal
            assetId={historyPosition.asset}
            assetSymbol={historyPosition.asset_symbol}
            onClose={() => setHistoryPosition(null)}
          />
        )}
      </div>
    );
  }

  // ── General overview ──────────────────────────────────────────────────────
  const totalDisponible = brokers.reduce((acc, b) => acc + parseFloat(b.cash_balance || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
          <button onClick={fetchPortfolioData} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Available cash per broker */}
        {brokers.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-4 h-4 text-green-600" />
              <p className="text-sm font-semibold text-gray-700">Available per broker</p>
              <span className="ml-auto text-sm font-bold text-green-600">{formatCurrency(totalDisponible)}</span>
            </div>
            <div className="space-y-2">
              {brokers.map((b) => {
                const bal = parseFloat(b.cash_balance || 0);
                return (
                  <div key={b.id} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{b.name}</span>
                    <span className={`text-sm font-bold ${bal >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {formatCurrency(Math.abs(bal), b.currency)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Goal buttons */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 px-1">Mis Goals</p>
          <div className="space-y-2">
            {goalGroups.length === 0 ? (
              <div className="text-center py-10">
                <PieChart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No hay goals con posiciones</p>
              </div>
            ) : (
              goalGroups.map((group) => {
                const gl = parseFloat(group.gain_loss || 0);
                const glColor = gl >= 0 ? 'text-green-600' : 'text-red-500';
                const glSign = gl >= 0 ? '+' : '';
                const currentValue = parseFloat(group.current_value || 0);
                return (
                  <button
                    key={group.goal_id}
                    onClick={() => setSelectedGoalId(group.goal_id)}
                    className="w-full bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between hover:shadow-md hover:bg-blue-50 transition text-left"
                  >
                    <div>
                      <p className="font-bold text-gray-900">{group.goal_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {group.positions.length} posición{group.positions.length !== 1 ? 'es' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{formatCurrency(currentValue)}</p>
                        <p className={`text-xs font-medium ${glColor}`}>
                          {glSign}{formatCurrency(Math.abs(gl))} ({glSign}{formatPercent(group.gain_loss_percentage || 0)})
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioScreen;
